import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useCommand, useHalState} from '@synako/haligator';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {LigneDePanier} from 'api-types/ffconnect';
import {parseValidPrice} from '@/utils/price-validation';
import NiceModal from '@ebay/nice-modal-react';
import {ConfirmationDegradationPrixMetierModal} from '@/domains/panier/PrixEditor/ConfirmationDegradationPrixMetierModal';
import {toDecimalFr, useLivraisonsPossiblesContext} from 'ff-common';
import {
  useDeroAutorisee,
  usePeutModifierPrix,
  usePeutVoirConfigurationPrixTolede,
} from '@/hooks/auth/usePermissionsCommerciales';
import {AjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {useClient} from '@/haligator-factory';

/** État d'édition de prix remonté au parent pour le preview des marges. */
export interface EditionPrixState {
  isEditing: boolean;
  /** Prix parseé ; null si saisie invalide / vide. */
  prixDraft: number | null;
}

const EDITION_PRIX_INITIAL: EditionPrixState = {
  isEditing: false,
  prixDraft: null,
};

/**
 * État local du parent de ligne pour relier l'éditeur de prix à MargesProduit.
 * `prixDraft` est déjà filtré hors édition (`undefined`) — à passer tel quel.
 */
export function useEditionPrixState(): {
  /** `undefined` hors édition ; `null` si saisie invalide ; `number` sinon. */
  prixDraft: number | null | undefined;
  onEditionPrixChange: (state: EditionPrixState) => void;
} {
  const [editionPrix, setEditionPrix] =
    useState<EditionPrixState>(EDITION_PRIX_INITIAL);
  const prixDraft = editionPrix.isEditing ? editionPrix.prixDraft : undefined;
  return {prixDraft, onEditionPrixChange: setEditionPrix};
}

interface UsePriceEditorOptions {
  ligneDePanier?: State<LigneDePanierDesc>;
  // false = affichage du prix total, édition désactivée. Défaut: true.
  isPrixUnitaire?: boolean;
  // Cadencier hors panier : crayon sans ligne, via `ajouterAvecPrixSaisi`.
  saisieAvantAjout?: boolean;
  ajouterAvecPrixSaisi?: AjouterAvecPrixSaisi;
  // Code article requis hors panier pour charger `configuration-prix-article`.
  codeArticle?: string;
  /** Notifie le parent (ex. pour MargesProduit) à chaque changement de saisie. */
  onEditionPrixChange?: (state: EditionPrixState) => void;
}

/**
 * Hook pour gérer l'édition des prix dans la prise de commande.
 * Encapsule l'état, les handlers et la logique métier pour la modification de prix inline.
 */
export const usePriceEditor = ({
  ligneDePanier,
  isPrixUnitaire = true,
  saisieAvantAjout = false,
  ajouterAvecPrixSaisi,
  codeArticle,
  onEditionPrixChange,
}: UsePriceEditorOptions) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const peutModifierPrix = usePeutModifierPrix();
  const deroAutorisee = useDeroAutorisee(saisieAvantAjout);
  const peutVoirConfigPrix = usePeutVoirConfigurationPrixTolede();
  const clientNav = useClient();
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();

  // État local pour l'édition du prix
  const [isEditing, setIsEditing] = useState(false);
  const [prixText, setPrixText] = useState('');
  // Prix affiché au moment de l'ouverture de l'édition, pour détecter une validation sans changement
  const [prixInitial, setPrixInitial] = useState<number | null>(null);
  // État pour le mode tarifaire : false = one_shot, true = dérogation
  const [isDerogation, setIsDerogation] = useState(false);
  const [isSavingHorsPanier, setIsSavingHorsPanier] = useState(false);
  const savingHorsPanierRef = useRef(false);

  // Effet pour initialiser le toggle avec la valeur existante depuis la base
  useEffect(() => {
    if (ligneDePanier?.data) {
      const modeTarifaireSouhaite = ligneDePanier.data.mode_tarifaire_souhaite;
      // Si mode_tarifaire_souhaite est défini, initialiser le toggle
      if (modeTarifaireSouhaite) {
        setIsDerogation(modeTarifaireSouhaite === 'saisir_dero');
      }
    }
  }, [ligneDePanier?.data.mode_tarifaire_souhaite, ligneDePanier?.data]);

  // Remonte le draft au parent pour le preview live des marges (props, pas de context)
  useEffect(() => {
    if (!onEditionPrixChange) {
      return;
    }
    if (!isEditing) {
      onEditionPrixChange({isEditing: false, prixDraft: null});
      return;
    }
    onEditionPrixChange({
      isEditing: true,
      prixDraft: parseValidPrice(prixText),
    });
  }, [isEditing, prixText, onEditionPrixChange]);

  useEffect(() => {
    return () => {
      onEditionPrixChange?.({isEditing: false, prixDraft: null});
    };
  }, [onEditionPrixChange]);

  // Config prix : ligne panier, ou article hors panier à l'ouverture de l'éditeur.
  const configPrixState = useHalState(
    ligneDePanier?.follow('configuration-prix-ligne-panier'),
  );
  const chargerConfigArticle =
    saisieAvantAjout &&
    !ligneDePanier &&
    isEditing &&
    peutVoirConfigPrix &&
    !!codeArticle;
  const configPrixArticleState = useHalState(
    chargerConfigArticle
      ? clientNav.follow('configuration-prix-article', {
          code_article: codeArticle,
          date_de_livraison:
            livraisonSelectionnee.data.tolede.date_de_livraison,
        })
      : undefined,
  );
  // Hors panier : la config Tolède part à l'ouverture du crayon. Tant qu'elle
  // charge, Valider reste inactif, sinon le premier clic est perdu.
  const configHorsPanierEnChargement =
    chargerConfigArticle && configPrixArticleState.loading;

  // Commande pour modifier le prix
  const modifierPrixCommand = useCommand(ligneDePanier, 'c:modifier-prix', {
    onSuccess: (event) => {
      textToast(t('back_office.prise_de_commande.panier.prix_modifie_succes'));
      setIsEditing(false);

      // Vérifier si une alerte de prix plancher dépassé est présente
      const alerte = event.data.data.alerte_prix_plancher_depasse;
      // `!= null` intentionnel : couvre null et undefined (events legacy).
      if (alerte != null) {
        const {prix_saisi, prix_plancher} = alerte;
        textToast(
          t('back_office.devis_personnalises.alerte_prix_plancher_depasse', {
            libelle: ligneDePanier?.data.libelle,
            code_article: ligneDePanier?.data.code_article,
            prix_saisi: toDecimalFr(prix_saisi),
            prix_plancher: toDecimalFr(prix_plancher),
          }),
          {duration: 999999999, variant: 'warning'},
        );
      }
    },
    onError,
  });

  // Checks consolidés exposés aux consumers (PrixBlock, PrixEditable, etc.)
  // pour éviter la duplication des règles d'édition.
  const categorieTarifApplique = configPrixState.data?.categorie_tarif_applique;
  const categorieBloque =
    categorieTarifApplique === 'M' ||
    categorieTarifApplique === 'N' ||
    categorieTarifApplique === 'S';
  const isPrixModifie = ligneDePanier?.data.prix_dero_souhaite != null;
  // Édition possible : commande HAL disponible + permission `changer-le-prix` + catégorie non-marché.
  const peutEditer =
    peutModifierPrix &&
    !categorieBloque &&
    (saisieAvantAjout || modifierPrixCommand.isAvailable);

  // Fonctions pour gérer l'édition
  const handleStartEdit = (prixActuel: number) => {
    if (!peutEditer) return;
    setIsEditing(true);
    setPrixInitial(prixActuel);
    // Remplacer le point par une virgule
    setPrixText(prixActuel.toString().replace('.', ','));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPrixText('');
    // Resync avec la valeur DB (mode_tarifaire_souhaite), pas un reset arbitraire à false
    setIsDerogation(
      ligneDePanier?.data.mode_tarifaire_souhaite === 'saisir_dero',
    );
  };

  const handleSave = () => {
    // Valider et parser le prix avec l'utilitaire
    const nouveauPrix = parseValidPrice(prixText);
    if (nouveauPrix === null) {
      textToast(t('global.erreur'));
      return;
    }

    if (!ligneDePanier) {
      if (!saisieAvantAjout || !ajouterAvecPrixSaisi) {
        textToast(t('global.erreur'));
        return;
      }
      if (savingHorsPanierRef.current) return;

      const modeTarifaireHorsPanier =
        isDerogation && deroAutorisee ? 'saisir_dero' : 'saisir_one_shot';
      // No-op : prix catalogue + one shot → ne pas ajouter (évite le tag « Prix saisi »).
      const prixInchangeHorsPanier =
        prixInitial !== null && nouveauPrix === prixInitial;
      if (
        prixInchangeHorsPanier &&
        modeTarifaireHorsPanier === 'saisir_one_shot'
      ) {
        handleCancel();
        return;
      }

      const soumettreHorsPanier = () => {
        savingHorsPanierRef.current = true;
        setIsSavingHorsPanier(true);
        void ajouterAvecPrixSaisi({
          prix: nouveauPrix,
          modeTarifaire: modeTarifaireHorsPanier,
        })
          .then((ajoute) => {
            if (ajoute) {
              setIsEditing(false);
              setPrixText('');
            }
          })
          .finally(() => {
            savingHorsPanierRef.current = false;
            setIsSavingHorsPanier(false);
          });
      };

      // La config Tolède décide de la modale métier. Pendant le chargement, Valider
      // est inactif : ce retour couvre la touche Entrée.
      if (configHorsPanierEnChargement) {
        return;
      }
      if (configPrixArticleState.data?.categorie_tarif_applique === 'C') {
        NiceModal.show(ConfirmationDegradationPrixMetierModal, {
          onConfirm: soumettreHorsPanier,
          onCancel: handleCancel,
        });
        return;
      }

      soumettreHorsPanier();
      return;
    }

    // Déterminer le mode tarifaire selon le choix de l'utilisateur
    const modeTarifaire = isDerogation ? 'saisir_dero' : 'saisir_one_shot';

    // No-op si prix et mode inchangés (évite de marquer « Prix saisi » à tort).
    const prixInchange = prixInitial !== null && nouveauPrix === prixInitial;
    const modeInchange = isPrixModifie
      ? modeTarifaire === ligneDePanier.data.mode_tarifaire_souhaite
      : !isDerogation;
    if (prixInchange && modeInchange) {
      handleCancel();
      return;
    }

    // Fonction pour soumettre le prix
    const submitPrix = () => {
      modifierPrixCommand.submit({
        prix_dero_souhaite: nouveauPrix,
        mode_tarifaire_souhaite: modeTarifaire,
      });
    };

    // Si c'est un prix métier (catégorie C) ET que le prix n'a pas encore été modifié, demander confirmation
    const prixDejaSaisi = ligneDePanier.data.prix_dero_souhaite != null;
    if (
      configPrixState.data?.categorie_tarif_applique === 'C' &&
      !prixDejaSaisi
    ) {
      NiceModal.show(ConfirmationDegradationPrixMetierModal, {
        onConfirm: () => {
          submitPrix();
        },
        onCancel: () => {
          handleCancel();
        },
      });
      return;
    }

    // Sinon, soumettre directement
    submitPrix();
  };

  // Fonction pour réinitialiser le prix
  const handleSupprimerPrix = () => {
    modifierPrixCommand.submit({prix_dero_souhaite: null});
  };

  const handleToggleDerogation = (value: boolean) => {
    setIsDerogation(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Calculer les permissions et valeurs d'affichage
  const getDisplayData = (ligneData: LigneDePanier) => {
    const prixDeroSouhaite = ligneData.prix_dero_souhaite;
    const modificationAutorisee =
      isPrixUnitaire && modifierPrixCommand.isAvailable;
    const prixAffiche = prixDeroSouhaite ?? ligneData.prix_unitaire;
    const isPrixModifie = prixDeroSouhaite != null;
    const displayLabel = isPrixModifie
      ? t('back_office.prise_de_commande.panier.prix_saisi')
      : null;

    return {
      prixAffiche,
      isPrixModifie,
      displayLabel,
      modificationAutorisee,
      uniteFacturationLigne: ligneData.unite_de_facturation,
      prixTotal: ligneData.prix_total_estime_ht,
    };
  };

  return {
    // État
    isEditing,
    prixText,
    setPrixText,
    isDerogation,
    utilisateurDeroAutorisee: saisieAvantAjout
      ? deroAutorisee
      : (configPrixState.data?.utilisateur_dero_autorisee ?? false),
    categorieTarifApplique,
    isPrixModifie,
    peutEditer,
    peutModifierPrix,
    isSavingHorsPanier,
    configHorsPanierEnChargement,

    // Commande
    modifierPrixCommand,

    // Handlers
    handleStartEdit,
    handleSave,
    handleCancel,
    handleSupprimerPrix,
    handleKeyDown,
    handleToggleDerogation,

    // Utilitaires
    getDisplayData,
  };
};
